import React, { useCallback, useEffect, useMemo, useState } from "react";
import { T, STATUS } from "../theme/tokens.js";
/* Semantic flags on the roster: red for over a full load, amber for spread
   across many programs. The app's status palette has no "critical"/"at risk"
   of its own, so these are the nearest honest stand-ins. */
const OVER = STATUS.overdue;     // red
const SPREAD = STATUS.inProgress; // amber
import { Panel, Btn, Notice, Labelled, Field, Empty, Kpi, Chip, Modal } from "./ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { useFallbackPoll } from "../lib/useLive.js";
import { fmtMoney } from "../lib/format.js";
import { buildRoster, rosterStats } from "../lib/roster.js";

/* The People page: the roster resolved across every contract.
 *
 * The registers hold people one contract at a time, which is right for a
 * contract's own page and useless for the one staffing question leadership
 * asks on a Monday, which is who is stretched across everything. This reads
 * every contract's resource rows at once, folds the same person into one row,
 * and floats to the top the two things only this view can see: who is on
 * several programs at once, and whose committed share across all of them is
 * past a hundred percent.
 *
 * Firm-wide and admin-maintained, by the same rule as the portfolio: contract
 * data never blends except on leadership's own cross-contract screens.
 */

const HIRE_TYPES = ["RFT", "On Call", "Part Time", "Intern"];
const EMPLOYMENT = ["Employee", "Subcontractor"];

export default function Roster({ contracts = [], isAdmin }) {
  const [workers, setWorkers] = useState(null);
  const [byContract, setByContract] = useState({});
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);     // worker being edited, or {} for a new one
  const [openId, setOpenId] = useState(null);        // which worker's assignments are expanded
  const [seedName, setSeedName] = useState("");

  const contractIds = useMemo(() => contracts.map((c) => c.id), [contracts]);

  const load = useCallback(async () => {
    const [w, r] = await Promise.all([
      supabase.from("workers").select("*").is("deleted_at", null).order("name"),
      contractIds.length
        ? supabase.from("resources").select("*").in("contract_id", contractIds).is("deleted_at", null)
        : Promise.resolve({ data: [] }),
    ]);
    if (w.error) { setError(friendlyError(w.error)); return; }
    if (r.error) { setError(friendlyError(r.error)); return; }
    setWorkers(w.data ?? []);
    const buckets = {};
    for (const row of r.data ?? []) {
      (buckets[row.contract_id] ||= []).push(row);
    }
    setByContract(buckets);
    setError("");
  }, [contractIds]);
  useEffect(() => { load(); }, [load]);
  /* Keep the roster current when someone else changes a person or an
     assignment. There is no single contract filter to ride the change feed
     with here, since the page reads every contract at once, so it asks again
     on a timer. Thirty seconds is slower than live and far better than stale. */
  useFallbackPoll(load, false, 30);

  const { people, unplaced } = useMemo(
    () => (workers ? buildRoster({ workers, byContract, contracts }) : { people: [], unplaced: [] }),
    [workers, byContract, contracts]);
  const stats = useMemo(() => rosterStats(people), [people]);

  /* No longer employed, or off the roster for good. The person is retired, not
     destroyed: deleted_at is a soft delete the owner can still recover, the
     same as every other row in the app. Their assignments are left as they
     are, so the history of who was on what is not rewritten. */
  const retire = async (id) => {
    const { error: e } = await supabase.from("workers")
      .update({ deleted_at: new Date().toISOString() }).eq("id", id);
    if (e) setError(friendlyError(e)); else { setEditing(null); await load(); }
  };

  /* No longer on this one project. The resource row is retired, which drops the
     assignment from the roster and from that contract's own Resources list, but
     leaves the person on the roster if they are still on other programs. */
  const removeAssignment = async (resourceId) => {
    const { error: e } = await supabase.from("resources")
      .update({ deleted_at: new Date().toISOString() }).eq("id", resourceId);
    if (e) setError(friendlyError(e)); else await load();
  };

  if (workers === null && !error) return <Panel title="People"><Empty text="Loading the roster..." /></Panel>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {error && <Notice kind="error">{error}</Notice>}

      <Panel
        title="People across every program"
        right={isAdmin ? <Btn small kind="gold" onClick={() => { setSeedName(""); setEditing({}); }}>Add a person</Btn> : null}
      >
        <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 14, lineHeight: 1.6 }}>
          One row per person, resolved from the resource rows on every contract. The people who
          need a staffing conversation sit at the top: anyone committed past a full workload across
          their programs, then anyone spread across three or more. Committed share is what was
          entered on each contract, added up.
          {isAdmin && (
            <> Open a person to see their programs and take them off one with Remove. For someone
            no longer employed, open Edit person and remove them from the roster entirely.</>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10, marginBottom: 16 }}>
          <Kpi label="On the roster" value={stats.headcount} sub="active people" />
          <Kpi label="Over a full load" value={stats.overallocated}
            sub="committed past 100%" color={stats.overallocated ? OVER?.ink : undefined} />
          <Kpi label="Spread thin" value={stats.spread} sub="on 3+ programs"
            color={stats.spread ? SPREAD?.ink : undefined} />
          <Kpi label="On more than one" value={stats.multiProgram} sub="shared across programs" />
        </div>

        {people.length === 0
          ? <Empty text={isAdmin
              ? "No one on the roster yet. Add people, or pull them in from your contracts below."
              : "No one on the roster yet."} />
          : <RosterTable people={people} openId={openId} setOpenId={setOpenId}
              isAdmin={isAdmin} onEdit={(w) => setEditing(w)} onRemoveAssignment={removeAssignment} />}
      </Panel>

      {isAdmin && unplaced.length > 0 && (
        <Panel title="In your contracts, not yet on the roster">
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 12, lineHeight: 1.6 }}>
            These names appear on contract resource lists but have no person on the roster yet.
            Add one and their assignments fold in automatically. People shown on the most programs
            first, since those are the ones worth resolving.
          </div>
          <div style={{ display: "grid", gap: 1, background: T.line,
            border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
            {unplaced.map((u) => (
              <div key={u.name} style={{ display: "grid", gridTemplateColumns: "1fr auto auto",
                gap: 10, alignItems: "center", background: T.panel, padding: "8px 12px" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{u.name}</span>
                <span style={{ display: "flex", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {u.contracts.map((c) => <Chip key={c}>{c}</Chip>)}
                </span>
                <Btn small kind="ghost" onClick={() => { setSeedName(u.name); setEditing({ name: u.name }); }}>
                  Add to roster
                </Btn>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {(adding || editing) && (
        <WorkerForm
          worker={editing}
          seedName={seedName}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSaved={async () => { setEditing(null); setAdding(false); await load(); }}
          onRetire={editing?.id ? () => retire(editing.id) : null}
          setError={setError}
        />
      )}
    </div>
  );
}

/* A remove that asks once. Removing a person or an assignment is the kind of
   thing a stray click should not do, so the button turns into a yes/no the
   first time, and only the yes acts. Both removes are soft deletes the owner
   can recover, but the confirmation is still the honest courtesy. */
function ConfirmButton({ label, confirm, onConfirm, kind = "ghost" }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return <Btn small kind={kind} onClick={(e) => { e.stopPropagation(); setAsking(true); }}>{label}</Btn>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11.5, color: T.soft }}>{confirm}</span>
      <Btn small kind="gold" onClick={(e) => { e.stopPropagation(); setAsking(false); onConfirm(); }}>Yes</Btn>
      <Btn small kind="ghost" onClick={(e) => { e.stopPropagation(); setAsking(false); }}>No</Btn>
    </span>
  );
}

function RosterTable({ people, openId, setOpenId, isAdmin, onEdit, onRemoveAssignment }) {
  return (
    <div style={{ display: "grid", gap: 1, background: T.line,
      border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 0.9fr 1.4fr 0.9fr auto",
        gap: 10, padding: "7px 12px", background: T.bg, fontSize: 10.5, fontWeight: 700,
        letterSpacing: "0.06em", textTransform: "uppercase", color: T.soft }}>
        <span>Person</span><span>Engagement</span><span>Programs</span><span>Committed</span><span />
      </div>
      {people.map((p) => {
        const open = openId === p.worker.id;
        return (
          <div key={p.worker.id} style={{ background: T.panel }}>
            <div
              onClick={() => setOpenId(open ? null : p.worker.id)}
              style={{ display: "grid", gridTemplateColumns: "1.6fr 0.9fr 1.4fr 0.9fr auto",
                gap: 10, alignItems: "center", padding: "9px 12px", cursor: "pointer",
                borderLeft: p.overallocated ? `3px solid ${OVER?.fill || T.gold}`
                  : p.spread ? `3px solid ${SPREAD?.fill || T.gold}` : "3px solid transparent" }}
            >
              <span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: T.navy }}>{p.name}</span>
                {p.keyPerson && (
                  <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
                    textTransform: "uppercase", color: p.overallocated ? (OVER?.ink) : (SPREAD?.ink) }}>
                    {p.overallocated ? "Over a full load" : "Key person"}
                  </span>
                )}
                {p.location && <div style={{ fontSize: 11.5, color: T.soft }}>{p.location}</div>}
              </span>
              <span style={{ fontSize: 12, color: T.ink }}>
                {p.hireType || p.employment || <span style={{ color: T.soft }}>—</span>}
                {p.company && <div style={{ fontSize: 11, color: T.soft }}>{p.company}</div>}
              </span>
              <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {p.programs.length
                  ? p.programs.map((c) => <Chip key={c}>{c}</Chip>)
                  : <span style={{ fontSize: 12, color: T.soft }}>No current assignment</span>}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                color: p.overallocated ? (OVER?.ink || T.navy) : T.navy }}>
                {p.combinedAllocation === null ? <span style={{ color: T.soft, fontWeight: 500 }}>not set</span>
                  : `${p.combinedAllocation}%`}
              </span>
              <span style={{ fontSize: 11, color: T.soft }}>{open ? "▾" : "▸"}</span>
            </div>

            {open && (
              <div style={{ padding: "4px 14px 12px 15px", background: T.bg }}>
                {p.assignments.length === 0
                  ? <div style={{ fontSize: 12, color: T.soft, padding: "6px 0" }}>
                      Not currently on any contract's resource list.
                    </div>
                  : (
                    <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
                      {p.assignments.map((a) => (
                        <div key={a.resourceId} style={{ display: "grid",
                          gridTemplateColumns: "1.4fr 1.3fr 0.7fr 0.7fr auto", gap: 10, alignItems: "center",
                          fontSize: 12.5, color: T.ink, padding: "6px 0",
                          borderBottom: `1px solid ${T.line}` }}>
                          <span style={{ fontWeight: 600, color: T.navy }}>{a.contractName}</span>
                          <span>{a.role || <span style={{ color: T.soft }}>role not set</span>}</span>
                          <span style={{ fontVariantNumeric: "tabular-nums" }}>
                            {a.rate === null ? <span style={{ color: T.soft }}>—</span> : `${fmtMoney(a.rate)}/hr`}
                          </span>
                          <span style={{ fontVariantNumeric: "tabular-nums" }}>
                            {a.allocationPct === null ? <span style={{ color: T.soft }}>no alloc.</span> : `${a.allocationPct}%`}
                          </span>
                          {isAdmin
                            ? <ConfirmButton label="Remove" confirm="Remove from this program?"
                                onConfirm={() => onRemoveAssignment(a.resourceId)} />
                            : <span />}
                        </div>
                      ))}
                    </div>
                  )}
                {p.rateLow !== null && (
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 8 }}>
                    Rate {p.rateLow === p.rateHigh ? fmtMoney(p.rateLow) : `${fmtMoney(p.rateLow)} to ${fmtMoney(p.rateHigh)}`}/hr
                    {p.workdayManager ? ` · reports to ${p.workdayManager}` : ""}
                  </div>
                )}
                {isAdmin && (
                  <div style={{ marginTop: 10 }}>
                    <Btn small kind="ghost" onClick={(e) => { e.stopPropagation(); onEdit(p.worker); }}>Edit person</Btn>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function WorkerForm({ worker, seedName, onClose, onSaved, onRetire, setError }) {
  const isNew = !worker?.id;
  const [d, setD] = useState({
    name: worker?.name || seedName || "",
    email: worker?.email || "",
    employment: worker?.employment || "",
    company: worker?.company || "",
    hire_type: worker?.hire_type || "",
    workday_manager: worker?.workday_manager || "",
    home_location: worker?.home_location || "",
    weekly_standard_hours: worker?.weekly_standard_hours ?? "",
    floating_holiday_date: worker?.floating_holiday_date || "",
    status: worker?.status || "Active",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!d.name.trim()) return;
    setBusy(true); setError("");
    const payload = {
      name: d.name.trim(),
      email: d.email.trim() || null,
      employment: d.employment.trim() || null,
      company: d.company.trim() || null,
      hire_type: d.hire_type.trim() || null,
      workday_manager: d.workday_manager.trim() || null,
      home_location: d.home_location.trim() || null,
      weekly_standard_hours: d.weekly_standard_hours === "" ? null : Number(d.weekly_standard_hours),
      floating_holiday_date: d.floating_holiday_date || null,
      status: d.status,
    };
    const q = isNew
      ? supabase.from("workers").insert(payload)
      : supabase.from("workers").update(payload).eq("id", worker.id);
    const { error: e } = await q;
    setBusy(false);
    if (e) setError(friendlyError(e)); else onSaved();
  };

  return (
    <Modal title={isNew ? "Add a person" : `Edit ${worker.name}`} onClose={onClose} wide>
      <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 14, lineHeight: 1.6 }}>
        The facts that belong to the person, not to any one assignment. Role, rate and dates stay
        on the resource row of each contract; what is here is how they are engaged and the single
        floating holiday that is theirs.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Labelled label="Name">
          <Field f={{ type: "text" }} value={d.name} autoFocus onChange={set("name")} />
        </Labelled>
        <Labelled label="Email" hint="Optional, to match them to an account later.">
          <Field f={{ type: "text" }} value={d.email} onChange={set("email")} placeholder="name@wittobriens.com" />
        </Labelled>
        <Labelled label="Engagement">
          <Field f={{ type: "select", options: EMPLOYMENT, allowOther: true }} value={d.employment} onChange={set("employment")} />
        </Labelled>
        <Labelled label="Company" hint="Their firm: WOB, or a sub such as TSG.">
          <Field f={{ type: "text" }} value={d.company} onChange={set("company")} placeholder="WOB" />
        </Labelled>
        <Labelled label="Hire type" hint="How leadership slices the roster.">
          <Field f={{ type: "select", options: HIRE_TYPES, allowOther: true }} value={d.hire_type} onChange={set("hire_type")} />
        </Labelled>
        <Labelled label="Workday manager">
          <Field f={{ type: "text" }} value={d.workday_manager} onChange={set("workday_manager")} />
        </Labelled>
        <Labelled label="Based in">
          <Field f={{ type: "text" }} value={d.home_location} onChange={set("home_location")} placeholder="USVI" />
        </Labelled>
        <Labelled label="Standard week" hint="Hours in a full week. Blank means 40.">
          <Field f={{ type: "number", suffix: "hrs" }} value={d.weekly_standard_hours} onChange={set("weekly_standard_hours")} />
        </Labelled>
        <Labelled label="Floating holiday" hint="Their one personal day, when chosen.">
          <Field f={{ type: "date" }} value={d.floating_holiday_date} onChange={set("floating_holiday_date")} />
        </Labelled>
        <Labelled label="Status">
          <Field f={{ type: "select", options: ["Active", "Inactive"] }} value={d.status} onChange={set("status")} />
        </Labelled>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
        <span>
          {onRetire && (
            <ConfirmButton label="Remove from roster"
              confirm="No longer employed? Remove them entirely?"
              onConfirm={onRetire} />
          )}
        </span>
        <span style={{ display: "flex", gap: 8 }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="gold" onClick={save} disabled={busy || !d.name.trim()}>
            {busy ? "Saving..." : isNew ? "Add person" : "Save"}
          </Btn>
        </span>
      </div>
    </Modal>
  );
}
